import { Icon } from '@iconify/react'
import { Show } from '@legendapp/state/react'
import { Link } from '@tanstack/react-router'
import { motion } from 'framer-motion'
import { isEmpty } from 'radash'
import { FC, isValidElement, ReactNode, useMemo } from 'react'

import {
  nativeMenuItemIconAnimationVariants,
  nativeMenuItemLabelAnimationVariants,
} from '~in-app/components/layout/console/native-sidebar/animation'
import { cn } from '~in-app/lib/utils'

interface NativeMenuItemProps {
  label: ReactNode | string
  icon?: ReactNode | string
  hasParent?: boolean
  path?: string
}

const NativeMenuItem: FC<NativeMenuItemProps> = ({ icon, label, path, hasParent }) => {
  const hasPath = useMemo(() => {
    return !isEmpty(path)
  }, [path])

  return (
    <li className="w-full">
      <Link
        disabled={!hasPath}
        to={path}
      >
        {
          ({ isActive }) => {
            const isExactActive = isActive && hasPath

            return (
              <div className={
                cn('flex items-center rounded-md px-4 py-2 text-muted-foreground hover:bg-muted', {
                  'bg-muted text-foreground': isExactActive && hasParent,
                  'bg-primary/5 dark:text-primary-400 text-primary-600 dark:bg-primary/20 dark:hover:bg-primary/30 hover:bg-primary/10': isExactActive && !hasParent,
                })
              }
              >
                <motion.div variants={nativeMenuItemIconAnimationVariants}>
                  <Show
                    if={isValidElement(icon)}
                    else={
                      (
                        <Show
                          if={typeof icon === 'string'}
                          else={
                            (
                              <Icon
                                height="18"
                                icon="lucide:dot"
                                width="18"
                              />
                            )
                          }
                        >
                          {
                            () => (
                              <Icon
                                height="18"
                                icon={icon as string}
                                width="18"
                              />
                            )
                          }
                        </Show>
                      )
                    }
                  >
                    {() => icon}
                  </Show>
                </motion.div>

                <motion.div
                  className="flex w-full items-center overflow-hidden"
                  variants={nativeMenuItemLabelAnimationVariants}
                >
                  <p className={
                    cn('min-w-0 flex-1 truncate text-sm font-normal select-none capitalize')
                  }
                  >
                    <Show
                      else={(label as string)}
                      if={isValidElement(label)}
                    >
                      {() => label}
                    </Show>
                  </p>
                </motion.div>
              </div>
            )
          }
        }
      </Link>
    </li>
  )
}
NativeMenuItem.displayName = 'ConsoleNativeMenuItem'

export { NativeMenuItem }
